import { Analytics } from '@cobliteam/analytics';
import type { InviteForm } from '@cobliteam/api-lib/src/__generated__/graphql';
import { UserRole } from '@cobliteam/common-types';
import useInvite from '@cobliteam/hooks/src/useEntity/useInvite';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useForm } from '@cobliteam/ui/form';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { DialogSetupGuideShell } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideShell';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { z } from 'zod';
import type { SetupGuideEntityFormProps } from '../entityModalConfig';
import SetupGuideFormRow from '../SetupGuideFormRow';
import UserRoleSelectField from './UserRoleSelectField';

/** Rows shown in the modal (Figma "Convide usuários"); only the first is required. */
const ROWS = 2;

const emailValidator = z.string().email();

/** Minimal "invite users" modal — up to two rows of e-mail + role, sent as one batch. */
const UsersForm = ({ fleetId, item, onClose, onSuccess, onAdvancedSettings }: SetupGuideEntityFormProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { mutateAsync: sendInvites, isPending } = useInvite().useSendInvites();

  const schema = useMemo(
    () =>
      z.object({
        rows: z.array(z.object({ email: z.string(), role: z.nativeEnum(UserRole) })).superRefine((rows, ctx) => {
          rows.forEach((row, index) => {
            const email = row.email.trim();
            if (!email) {
              // Only the first row is mandatory; later rows may stay empty.
              if (index === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'email'], message: t('entity_modal.users.validation.email_required') });
              return;
            }
            if (!emailValidator.safeParse(email).success) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'email'], message: t('entity_modal.users.validation.email_invalid') });
              return;
            }
            const isRepeated = rows.some((other, otherIndex) => otherIndex < index && other.email.trim().toLowerCase() === email.toLowerCase());
            if (isRepeated) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'email'], message: t('entity_modal.users.validation.email_duplicated') });
          });
        }),
      }),
    [t]
  );

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { rows: Array.from({ length: ROWS }, () => ({ email: '', role: UserRole.MANAGER })) },
  });

  const handleSubmit = async ({ rows }: FormValues) => {
    const invites: InviteForm[] = rows.filter((row) => row.email.trim()).map((row) => ({ email_address: row.email.trim(), role: row.role, groups_restriction: [] }));
    // The invites hook resolves (with response.errors) instead of rejecting on a GraphQL failure — and
    // already toasts both outcomes — so only a clean response advances to the success modal.
    const response = await sendInvites(invites).catch(() => ({ errors: true }));
    if (response?.errors) return;
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'users', count: invites.length });
    onSuccess(invites.length);
  };

  return (
    <DialogSetupGuideShell
      open
      onClose={onClose}
      title={t('entity_modal.users.title')}
      description={t('entity_modal.users.description')}
      secondaryAction={{ label: t('entity_modal.advanced_settings'), onClick: onAdvancedSettings }}
      primaryAction={{
        label: t('entity_modal.users.submit'),
        onClick: form.handleSubmit(handleSubmit),
        disabled: !form.formState.isValid || isPending,
        loading: isPending,
        endIcon: <Icon name='send-horizontal' size='sm' />,
        'data-testid': 'setup-guide-users-submit',
      }}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <div className='flex flex-col'>
          <Tag color='default' size='sm' label={t('entity_modal.users.progress', { current: item.current, target: item.target })} />
          {Array.from({ length: ROWS }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size row list, never reordered
            <SetupGuideFormRow key={index} label={t('entity_modal.users.row_label', { index: index + 1 })}>
              <FormField control={form.control} name={`rows.${index}.email`}>
                {({ field }) => (
                  <FormItem className='w-72'>
                    <FormLabel>{t('entity_modal.users.email_label')}</FormLabel>
                    <FormControl>
                      <Input {...field} type='email' placeholder={t('entity_modal.users.email_placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              </FormField>
              <FormField control={form.control} name={`rows.${index}.role`}>
                {({ field }) => (
                  <FormItem className='w-64'>
                    <FormLabel>{t('entity_modal.users.role_label')}</FormLabel>
                    <FormControl>
                      <UserRoleSelectField value={field.value} onChange={field.onChange} placeholder={t('entity_modal.users.role_placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              </FormField>
            </SetupGuideFormRow>
          ))}
        </div>
      </Form>
    </DialogSetupGuideShell>
  );
};

export default UsersForm;
